import { Link } from 'react-router-dom';
import type { StorefrontSection } from '@mlm/shared';
import { useFeaturedPackages, useProductHref } from '../../lib/hooks';
import { peso } from '../../lib/format';
import { Placeholder } from '../placeholder';

type FeaturedPackagesConfig = Extract<StorefrontSection, { type: 'featuredPackages' }>['config'];

// Static Tailwind strings (never `grid-cols-${x}`, which the purge drops).
const COLS: Record<3 | 4 | 5, string> = {
  3: 'grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-2 lg:grid-cols-4',
  5: 'grid-cols-2 lg:grid-cols-5',
};

/**
 * Featured Packages strip — the admin-curated, ORDERED products chosen on the
 * Featured Packages page. Renders only when enabled + non-empty. Heading
 * precedence: section-config override → feature-level heading → default.
 */
export function FeaturedPackagesSection({ section }: { section: StorefrontSection }) {
  const hrefFor = useProductHref();
  const cfg = section.config as FeaturedPackagesConfig;
  const featured = useFeaturedPackages();
  if (!featured.enabled) return null;
  const heading = cfg.heading ?? featured.heading ?? 'Featured Packages';
  const cols = COLS[cfg.cols ?? 5];

  return (
    <section className="mt-4 rounded-[4px] bg-white">
      <div className="flex flex-wrap items-center gap-[18px] p-4">
        <span className="text-[21px] font-extrabold text-[#333]">{heading}</span>
        {cfg.subheading && (
          <span className="text-[13px] font-medium text-[#888]">{cfg.subheading}</span>
        )}
      </div>
      <div className={`grid ${cols} px-2 pb-4`}>
        {featured.products.map((p, i) => (
          <Link key={p.id} to={hrefFor(p)} className="group block p-2">
            <div className="relative aspect-square overflow-hidden rounded-[4px] transition-shadow group-hover:shadow-[0_6px_16px_rgba(0,0,0,.12)]">
              <Placeholder i={i} src={p.image} alt={p.name} />
            </div>
            <div className="mt-[7px] line-clamp-2 h-[33px] text-[12.5px] leading-[1.3] text-[#333]">
              {p.name}
            </div>
            <div className="mt-1 text-[17px] font-extrabold text-o">
              {peso(p.price)}
              {p.was > 0 && (
                <span className="ml-1.5 text-xs font-medium text-[#aaa] line-through">
                  {peso(p.was)}
                </span>
              )}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
